import { z } from "zod";
import { Timestamps } from "../common/timestamps.schema.ts";
import { CustomerId } from "../fsm/fsm.schema.ts";
import { LocationId } from "../locations/locations.schema.ts";

// core_stations (BEL-581, ADR 042 §1) — a physical parking spot within a
// location. Optional per customer: a customer may have zero stations (Ikos).
export const StationId = z.string().uuid().brand<"StationId">();
export type StationId = z.infer<typeof StationId>;

// 'geofence' implemented; 'analog_code'/'digital_single_point' reserved
// (ADR 042 Decision §1, same spelling as StationType in api's partner.enums.ts).
export const StationType = z.enum([
  "geofence",
  "analog_code",
  "digital_single_point",
]);
export type StationType = z.infer<typeof StationType>;

// Standard GeoJSON position: [lng, lat] (+ optional altitude), both numeric
// (RFC 7946 §3.1.1). No PostGIS in Phase 1 (ADR 042 §1) — jsonb is sufficient
// for registration-only scope; this schema is the only validation of shape.
const GeoJsonPosition = z.array(z.number()).min(2).max(3);

// A linear ring: >=4 positions, first and last identical (closed ring,
// RFC 7946 §3.1.6). BEL-582 requirement #2 — malformed geometry (open ring,
// non-numeric coordinates, wrong GeoJSON type) must be rejected here, before
// it reaches the DB's `core_stations_geofence_geometry_check` (which only
// asserts non-null, not shape).
const GeoJsonLinearRing = z
  .array(GeoJsonPosition)
  .min(4, "A linear ring needs at least 4 positions (closed ring)")
  .refine(
    (ring) => {
      const first = ring[0];
      const last = ring[ring.length - 1];
      return (
        first !== undefined &&
        last !== undefined &&
        first.length === last.length &&
        first.every((v, i) => v === last[i])
      );
    },
    { message: "Linear ring must be closed (first and last position equal)" },
  );

const GeoJsonPolygon = z.object({
  type: z.literal("Polygon"),
  coordinates: z.array(GeoJsonLinearRing).min(1),
});

const GeoJsonMultiPolygon = z.object({
  type: z.literal("MultiPolygon"),
  coordinates: z.array(z.array(GeoJsonLinearRing).min(1)).min(1),
});

// GeoJSON Polygon/MultiPolygon (RFC 7946) — the only two geometry types a
// `geofence` station accepts in Phase 1 (ADR 042 §1).
export const StationGeometry = z.union([GeoJsonPolygon, GeoJsonMultiPolygon]);
export type StationGeometry = z.infer<typeof StationGeometry>;

export const StationDto = z
  .object({
    id: StationId,
    customerId: CustomerId,
    locationId: LocationId,
    name: z.string(),
    type: StationType,
    // Nullable in the DB in general, but required by a CHECK constraint when
    // type='geofence' (enforced at the DB layer, not re-validated here).
    geometry: StationGeometry.nullable(),
    isActive: z.boolean().default(true),
    deactivatedAt: z.string().nullable().optional(),
    deactivatedBy: z.string().uuid().nullable().optional(),
  })
  .merge(Timestamps);
export type StationDto = z.infer<typeof StationDto>;

// Fase 1 (BEL-582 Alcance): only `geofence` accepts effective writes.
// `analog_code`/`digital_single_point` are reserved enum values (Fase 2,
// BEL-588) — CreateStationInput/UpdateStationInput don't accept them yet.
export const CreateStationInput = z.object({
  locationId: LocationId,
  name: z.string().min(1),
  type: z.literal("geofence").default("geofence"),
  geometry: StationGeometry,
});
export type CreateStationInput = z.infer<typeof CreateStationInput>;

export const UpdateStationInput = z.object({
  name: z.string().min(1).optional(),
  geometry: StationGeometry.optional(),
});
export type UpdateStationInput = z.infer<typeof UpdateStationInput>;
